import React, { useState, useEffect, useMemo } from "react";
import httpClient from "../../services/httpClient.js";
import Logger from "../../utils/Logger";
import { LoadingComponent } from "../../App.js";
import { useNotification } from "../../components/NotificationContext";
import orangeSortIcon from "../../media/orange-sort-icon.svg";
import sortIcon from "../../media/sort-icon.svg";
import editButtonIcon from "../../media/edit-button.svg";
import agreeButtonIcon from "../../media/agree-button.svg";
import backButtonIcon from "../../media/back-button.svg";
import { downloadFile } from "../../services/download.js";

const ScheduleAdmin = () => {
  const { showNotification } = useNotification();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [allScheduleData, setAllScheduleData] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredPrevIcon, setHoveredPrevIcon] = useState(false);
  const [hoveredNextIcon, setHoveredNextIcon] = useState(false);
  const [isMonthLocked, setIsMonthLocked] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isSavingEntry, setIsSavingEntry] = useState(false);
  const [hoveredPdfButton, setHoveredPdfButton] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [fullMonthUserIds, setFullMonthUserIds] = useState(() => new Set());

  const monthNames = [
    "Styczeń",
    "Luty",
    "Marzec",
    "Kwiecień",
    "Maj",
    "Czerwiec",
    "Lipiec",
    "Sierpień",
    "Wrzesień",
    "Październik",
    "Listopad",
    "Grudzień",
  ];

  const absenceTypes = [
    { value: "", label: "Standardowy dzień pracy" },
    { value: "university", label: "Zajęcia na uczelni" },
    { value: "holiday", label: "Święto" },
    { value: "personal", label: "Osobiste" },
  ];

  const getAbsenceTypeLabel = (absenceType) => {
    return (
      absenceTypes.find((type) => type.value === absenceType)?.label ||
      "Standardowy dzień pracy"
    );
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const toggleFullMonth = (userId) => {
    setFullMonthUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const buildFullMonthEntries = (entries, userId) => {
    const pad = (value) => value.toString().padStart(2, "0");
    const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
    const entriesByDay = {};

    entries.forEach((entry) => {
      const dateKey = normalizeDateKey(entry.date);
      if (!dateKey) return;
      const day = Number(dateKey.split("-")[2]);
      if (day) {
        entriesByDay[day] = entry;
      }
    });

    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      const dateKey = `${selectedYear}-${pad(selectedMonth + 1)}-${pad(day)}`;
      return (
        entriesByDay[day] || {
          id: null,
          userId,
          date: dateKey,
          remoteHours: 0,
          onsiteHours: 0,
          absenceType: "",
          comment: null,
          hrComment: null,
        }
      );
    });
  };

  const getEntriesForDisplay = (entries, showFullMonth, userId) => {
    const sortedEntries = [...entries].sort((a, b) =>
      (normalizeDateKey(a.date) || "").localeCompare(
        normalizeDateKey(b.date) || "",
      ),
    );

    if (!showFullMonth) {
      return sortedEntries;
    }

    return buildFullMonthEntries(sortedEntries, userId);
  };

  const getEntryEditKey = (entry) => {
    if (entry?.id) return entry.id;
    const dateKey = normalizeDateKey(entry?.date);
    if (dateKey && entry?.userId) {
      return `new:${entry.userId}:${dateKey}`;
    }
    return null;
  };

  const parseDateOnly = (dateValue) => {
    const key = normalizeDateKey(dateValue);
    if (!key) return null;
    const [year, month, day] = key.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const normalizeDateKey = (dateValue) => {
    if (typeof dateValue === "string") {
      return dateValue.slice(0, 10);
    }
    if (dateValue && typeof dateValue === "object") {
      const year = dateValue.year ?? dateValue.Year;
      const month = dateValue.month ?? dateValue.Month;
      const day = dateValue.day ?? dateValue.Day;
      if (year && month && day) {
        const pad = (value) => value.toString().padStart(2, "0");
        return `${year}-${pad(month)}-${pad(day)}`;
      }
    }
    return null;
  };

  const formatDateOnly = (dateValue) => {
    const key = normalizeDateKey(dateValue);
    if (!key) return "-";
    const [year, month, day] = key.split("-");
    return `${day}.${month}.${year}`;
  };

  const parseHours = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const toTimeSpanHHmm = (hoursDecimal) => {
    const safeHours =
      Number.isFinite(hoursDecimal) && hoursDecimal > 0 ? hoursDecimal : 0;
    const totalMinutes = Math.round(safeHours * 60);
    const hh = Math.floor(totalMinutes / 60)
      .toString()
      .padStart(2, "0");
    const mm = (totalMinutes % 60).toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const mapAbsenceTypeToDayType = (absenceType) => {
    if (!absenceType) return "Regular";

    const mapping = {
      university: "University",
      holiday: "Holiday",
      personal: "Personal",
    };

    return mapping[absenceType] || "Regular";
  };

  const handleDownloadUsersPdf = async () => {
    if (isDownloadingPdf) return;

    try {
      setIsDownloadingPdf(true);
      await downloadFile(
        `/Worklogs/report/pdf?year=${selectedYear}&month=${selectedMonth + 1}`,
        () => {},
        (progress) => {
          if (progress > 0) {
            Logger.info(`Pobieranie PDF: ${progress}%`);
          }
        },
      );
      showNotification("Dane użytkowników zostały pobrane", "success");
    } catch (error) {
      Logger.error("Error downloading users PDF:", error);
      showNotification("Błąd podczas pobierania danych użytkowników", "error");
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await httpClient.get("/Users/role/Student", {
        withAuth: true,
      });
      setUsers(response.data || []);
      Logger.log("Student users fetched:", response.data);
    } catch (error) {
      Logger.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const fetchAllScheduleData = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get(
        `/Worklogs/report?year=${selectedYear}&month=${selectedMonth + 1}`,
        { withAuth: true },
      );
      setAllScheduleData(response.data || []);
      Logger.log("All schedule data fetched:", response.data);
    } catch (error) {
      Logger.error("Error fetching all schedule data:", error);
      setAllScheduleData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserScheduleData = async (userId) => {
    setLoading(true);
    try {
      const response = await httpClient.get(
        `/Worklogs/report?year=${selectedYear}&month=${selectedMonth + 1}&userId=${userId}`,
        { withAuth: true },
      );
      setAllScheduleData(response.data || []);
      Logger.log("User schedule data fetched:", response.data);
    } catch (error) {
      Logger.error("Error fetching user schedule data:", error);
      setAllScheduleData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLockStatus = async () => {
    try {
      const response = await httpClient.get(
        `/Worklogs/lock?year=${selectedYear}&month=${selectedMonth + 1}`,
        { withAuth: true },
      );
      setIsMonthLocked(response.data === true);
      Logger.log("Lock status fetched:", response.data);
    } catch (error) {
      Logger.error("Error fetching lock status:", error);
      // Jeśli błąd, zakładamy że miesiąc nie jest zablokowany
      setIsMonthLocked(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setEditingEntryId(null);
    setEditingEntry(null);
    setFullMonthUserIds(new Set());
    if (selectedUser) {
      fetchUserScheduleData(selectedUser.id);
    } else {
      fetchAllScheduleData();
    }
    fetchLockStatus();
  }, [selectedMonth, selectedYear, selectedUser]);

  const handleMonthChange = (direction) => {
    if (direction === "prev") {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const handleToggleLock = async () => {
    const newLockState = !isMonthLocked;
    try {
      if (newLockState) {
        // Lock the month
        await httpClient.post(
          `/Worklogs/lock?year=${selectedYear}&month=${selectedMonth + 1}`,
          null,
          { withAuth: true },
        );
      } else {
        // Unlock the month
        await httpClient.delete(
          `/Worklogs/lock?year=${selectedYear}&month=${selectedMonth + 1}`,
          { withAuth: true },
        );
      }
      setIsMonthLocked(newLockState);
    } catch (error) {
      Logger.error("Error toggling month lock:", error);
    }
  };

  const handleEditEntry = (entry) => {
    const entryKey = getEntryEditKey(entry);
    if (!entryKey) return;

    setEditingEntryId(entryKey);
    setEditingEntry({
      remoteHours:
        entry.remoteHours !== undefined && entry.remoteHours !== null
          ? String(entry.remoteHours)
          : "",
      onsiteHours:
        entry.onsiteHours !== undefined && entry.onsiteHours !== null
          ? String(entry.onsiteHours)
          : "",
      absenceType: entry.absenceType || "",
      hrComment: entry.hrComment || "",
    });
  };

  const handleCancelEdit = () => {
    setEditingEntryId(null);
    setEditingEntry(null);
  };

  const refreshScheduleData = async () => {
    const url = selectedUser
      ? `/Worklogs/report?year=${selectedYear}&month=${selectedMonth + 1}&userId=${selectedUser.id}`
      : `/Worklogs/report?year=${selectedYear}&month=${selectedMonth + 1}`;
    const response = await httpClient.get(url, { withAuth: true });
    const data = response.data || [];
    setAllScheduleData(data);
    return data;
  };

  const buildWorklogPayload = (dateKey, editingEntry, studentComment = "") => ({
    date: dateKey,
    remoteTime: toTimeSpanHHmm(parseHours(editingEntry.remoteHours)),
    officeTime: toTimeSpanHHmm(parseHours(editingEntry.onsiteHours)),
    dayType: mapAbsenceTypeToDayType(editingEntry.absenceType),
    notes: studentComment || "",
  });

  const createWorklogForUser = async (userId, dateKey, editingEntry, studentComment) => {
    await httpClient.post(
      "/Worklogs/for-user",
      {
        userId,
        ...buildWorklogPayload(dateKey, editingEntry, studentComment),
      },
      { withAuth: true },
    );
  };

  const handleSaveEntry = async (entry) => {
    const entryKey = getEntryEditKey(entry);
    if (!editingEntry || editingEntryId !== entryKey || isSavingEntry) return;

    const remoteHours = parseHours(editingEntry.remoteHours);
    const onsiteHours = parseHours(editingEntry.onsiteHours);

    if (
      remoteHours < 0 ||
      onsiteHours < 0 ||
      remoteHours > 24 ||
      onsiteHours > 24
    ) {
      showNotification("Nieprawidłowe godziny (0–24)", "error");
      return;
    }

    const dateKey = normalizeDateKey(entry.date);
    if (!dateKey) {
      showNotification("Nieprawidłowa data wpisu", "error");
      return;
    }

    const dataToSave = buildWorklogPayload(dateKey, editingEntry, entry.comment);

    setIsSavingEntry(true);
    try {
      if (entry.id) {
        await httpClient.put(`/Worklogs/${entry.id}`, dataToSave, {
          withAuth: true,
        });
        await httpClient.put(
          `/Worklogs/${entry.id}/hr-comment`,
          { hrComment: editingEntry.hrComment || "" },
          { withAuth: true },
        );
      } else {
        if (!entry.userId) {
          showNotification("Brak identyfikatora użytkownika", "error");
          return;
        }

        await createWorklogForUser(
          entry.userId,
          dateKey,
          editingEntry,
          entry.comment,
        );

        const hrComment = editingEntry.hrComment?.trim();
        if (hrComment) {
          const refreshedData = await refreshScheduleData();
          const createdEntry = refreshedData.find(
            (item) =>
              item.userId === entry.userId &&
              normalizeDateKey(item.date) === dateKey,
          );
          if (createdEntry?.id) {
            await httpClient.put(
              `/Worklogs/${createdEntry.id}/hr-comment`,
              { hrComment },
              { withAuth: true },
            );
          }
        }
      }

      setEditingEntryId(null);
      setEditingEntry(null);
      showNotification("Zapisano zmiany", "success");
      await refreshScheduleData();
    } catch (error) {
      Logger.error("Error saving schedule entry:", error);
      showNotification("Błąd podczas zapisywania wpisu", "error");
    } finally {
      setIsSavingEntry(false);
    }
  };

  const renderCommentCell = (entry, isEditing) => {
    if (isEditing) {
      return (
        <td className="comment-cell comment-cell--stacked">
          {entry.comment ? (
            <div className="student-comment">{entry.comment}</div>
          ) : (
            <div className="student-comment student-comment--empty">—</div>
          )}
          <input
            type="text"
            className="hr-comment-input"
            value={editingEntry.hrComment}
            onChange={(e) =>
              setEditingEntry({
                ...editingEntry,
                hrComment: e.target.value,
              })
            }
            placeholder="Komentarz HR..."
          />
        </td>
      );
    }

    return (
      <td className="comment-cell comment-cell--stacked">
        {entry.comment ? (
          <div className="student-comment">{entry.comment}</div>
        ) : null}
        {entry.hrComment ? (
          <div className="hr-comment">{entry.hrComment}</div>
        ) : null}
        {!entry.comment && !entry.hrComment ? "—" : null}
      </td>
    );
  };

  const renderActionCell = (entry, isEditing) => {
    const entryKey = getEntryEditKey(entry);
    if (!entryKey) {
      return <td>—</td>;
    }

    return (
    <td>
      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
        {isEditing ? (
          <>
            <img
              className="agree-btn"
              src={agreeButtonIcon}
              alt="Zapisz"
              style={{
                cursor: isSavingEntry ? "not-allowed" : "pointer",
                width: "30px",
                height: "30px",
                opacity: isSavingEntry ? 0.5 : 1,
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (!isSavingEntry) handleSaveEntry(entry);
              }}
            />
            <img
              className="back-btn"
              src={backButtonIcon}
              alt="Anuluj"
              style={{ cursor: "pointer", width: "40px", height: "40px" }}
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
            />
          </>
        ) : (
          <img
            className="edit-btn"
            src={editButtonIcon}
            alt="Edytuj"
            style={{
              cursor:
                editingEntryId && editingEntryId !== entryKey
                  ? "not-allowed"
                  : "pointer",
              width: "36px",
              height: "36px",
              opacity:
                editingEntryId && editingEntryId !== entryKey ? 0.3 : 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!editingEntryId || editingEntryId === entryKey) {
                handleEditEntry(entry);
              }
            }}
          />
        )}
      </div>
    </td>
    );
  };

  const renderEntryRow = (entry, { showDayName = false } = {}) => {
    const entryKey = getEntryEditKey(entry);
    const isEditing = entryKey && editingEntryId === entryKey;
    const date = parseDateOnly(entry.date);
    if (!date) return null;

    const dayName = date.toLocaleDateString("pl-PL", { weekday: "short" });
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    const remoteHours = isEditing
      ? parseHours(editingEntry.remoteHours)
      : parseHours(entry.remoteHours);
    const onsiteHours = isEditing
      ? parseHours(editingEntry.onsiteHours)
      : parseHours(entry.onsiteHours);

    return (
      <tr
        key={entry.id || `day-${normalizeDateKey(entry.date)}`}
        className={`
          ${showDayName && isWeekend ? "weekend-row" : ""}
          ${entry.absenceType ? `absence-type-${entry.absenceType}` : ""}
        `.trim()}
      >
        {showDayName && <td>{dayName}</td>}
        <td>{formatDateOnly(entry.date)}</td>
        <td>
          {isEditing ? (
            <select
              value={editingEntry.absenceType}
              onChange={(e) =>
                setEditingEntry({
                  ...editingEntry,
                  absenceType: e.target.value,
                })
              }
              className="absence-type-select"
            >
              {absenceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          ) : (
            <span
              className={`absence-type-badge ${entry.absenceType || ""}`}
            >
              {getAbsenceTypeLabel(entry.absenceType)}
            </span>
          )}
        </td>
        <td>
          {isEditing ? (
            <input
              type="number"
              step="0.5"
              min="0"
              max="24"
              value={editingEntry.remoteHours}
              onChange={(e) =>
                setEditingEntry({
                  ...editingEntry,
                  remoteHours: e.target.value,
                })
              }
              className="hour-input"
            />
          ) : (
            <span>{remoteHours.toFixed(2)}h</span>
          )}
        </td>
        <td>
          {isEditing ? (
            <input
              type="number"
              step="0.5"
              min="0"
              max="24"
              value={editingEntry.onsiteHours}
              onChange={(e) =>
                setEditingEntry({
                  ...editingEntry,
                  onsiteHours: e.target.value,
                })
              }
              className="hour-input"
            />
          ) : (
            <span>{onsiteHours.toFixed(2)}h</span>
          )}
        </td>
        <td>
          <strong>{(remoteHours + onsiteHours).toFixed(2)}h</strong>
        </td>
        {renderCommentCell(entry, isEditing)}
        {renderActionCell(entry, isEditing)}
      </tr>
    );
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(term) ||
        user.lastName?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term),
    );
  }, [users, searchTerm]);

  const groupedByUser = useMemo(() => {
    const grouped = {};
    const usersById = users.reduce((acc, user) => {
      acc[user.id] = user;
      return acc;
    }, {});

    allScheduleData.forEach((entry) => {
      const userId = entry.userId;
      if (!grouped[userId]) {
        const userFromList = usersById[userId];
        grouped[userId] = {
          fullName:
            entry.fullName ||
            `${userFromList?.firstName || ""} ${userFromList?.lastName || ""}`.trim(),
          entries: [],
          totalRemote: 0,
          totalOnsite: 0,
          totalHours: 0,
        };
      }
      grouped[userId].entries.push(entry);
      grouped[userId].totalRemote += entry.remoteHours || 0;
      grouped[userId].totalOnsite += entry.onsiteHours || 0;
      grouped[userId].totalHours +=
        (entry.remoteHours || 0) + (entry.onsiteHours || 0);
    });
    return grouped;
  }, [allScheduleData, users]);

  const totalSummary = useMemo(() => {
    return Object.values(groupedByUser).reduce(
      (acc, userGroup) => {
        acc.remote += userGroup.totalRemote;
        acc.onsite += userGroup.totalOnsite;
        acc.total += userGroup.totalHours;
        return acc;
      },
      { remote: 0, onsite: 0, total: 0 },
    );
  }, [groupedByUser]);

  const selectedUserShowFullMonth = selectedUser
    ? fullMonthUserIds.has(selectedUser.id)
    : false;
  const selectedUserDisplayEntries = selectedUser
    ? getEntriesForDisplay(
        groupedByUser[selectedUser.id]?.entries || [],
        selectedUserShowFullMonth,
        selectedUser.id,
      )
    : [];

  if (loading && !allScheduleData.length) {
    return <LoadingComponent />;
  }

  return (
    <div className="schedule-container schedule-admin">
      <div className="schedule-header">
        <h1>Harmonogram pracy</h1>
        <div className="month-selector">
          <div
            onClick={() => handleMonthChange("prev")}
            onMouseEnter={() => setHoveredPrevIcon(true)}
            onMouseLeave={() => setHoveredPrevIcon(false)}
            className="month-nav-icon"
            style={{
              backgroundImage: `url(${orangeSortIcon})`,
              transform: hoveredPrevIcon ? "rotate(180deg)" : "rotate(90deg)",
              cursor: "pointer",
            }}
          />
          <span className="month-display">
            {monthNames[selectedMonth]} {selectedYear}
          </span>
          <div
            onClick={() => handleMonthChange("next")}
            onMouseEnter={() => setHoveredNextIcon(true)}
            onMouseLeave={() => setHoveredNextIcon(false)}
            className="month-nav-icon"
            style={{
              backgroundImage: `url(${orangeSortIcon})`,
              transform: hoveredNextIcon ? "rotate(180deg)" : "rotate(270deg)",
              cursor: "pointer",
            }}
          />
        </div>
      </div>

      <div className="lock-month-section">
        <button
          onClick={handleToggleLock}
          className={`lock-month-btn ${isMonthLocked ? "locked" : "unlocked"}`}
        >
          {isMonthLocked
            ? "Miesiąc zablokowany - Kliknij aby odblokować"
            : "Miesiąc otwarty - Kliknij aby zablokować"}
        </button>
      </div>

      <div className="user-filter-section">
        <div className="user-search">
          <input
            type="text"
            placeholder="Szukaj studenta..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="user-selector">
          <button
            onClick={() => setSelectedUser(null)}
            className={`user-filter-btn ${!selectedUser ? "active" : ""}`}
          >
            Wszyscy studenci
          </button>
          {filteredUsers.map((user) => (
            <button
              key={user.id}
              onClick={() => setSelectedUser(user)}
              className={`user-filter-btn ${
                selectedUser?.id === user.id ? "active" : ""
              }`}
            >
              {user.firstName} {user.lastName}
            </button>
          ))}
        </div>
      </div>

      <div className="pdf-download-section">
        <div className="button-container">
          <button
            type="button"
            className="booking-button"
            onClick={handleDownloadUsersPdf}
            disabled={isDownloadingPdf}
            onMouseEnter={() => setHoveredPdfButton(true)}
            onMouseLeave={() => setHoveredPdfButton(false)}
          >
            <div
              className="icon"
              style={{
                backgroundImage: `url(${
                  hoveredPdfButton ? orangeSortIcon : sortIcon
                })`,
                transform: hoveredPdfButton ? "scale(1.2)" : "rotate(270deg)",
              }}
            />
            Pobierz dane użytkowników
          </button>
        </div>
      </div>

      {!selectedUser && (
        <div className="schedule-summary">
          <div className="summary-card">
            <h3>Podsumowanie wszystkich użytkowników</h3>
            <div className="summary-row">
              <span>Liczba użytkowników:</span>
              <strong>{Object.keys(groupedByUser).length}</strong>
            </div>
            <div className="summary-row">
              <span>Suma godzin zdalnych:</span>
              <strong>{totalSummary.remote.toFixed(2)}h</strong>
            </div>
            <div className="summary-row">
              <span>Suma godzin stacjonarnych:</span>
              <strong>{totalSummary.onsite.toFixed(2)}h</strong>
            </div>
            <div className="summary-row total">
              <span>Suma wszystkich godzin:</span>
              <strong>{totalSummary.total.toFixed(2)}h</strong>
            </div>
          </div>
        </div>
      )}

      {!selectedUser ? (
        // Show all users grouped
        <div className="users-schedule-list">
          {Object.keys(groupedByUser).length === 0 ? (
            <div className="no-data-message">
              Brak danych dla wybranego miesiąca
            </div>
          ) : (
            Object.entries(groupedByUser).map(([userId, userGroup]) => {
              const showFullMonth = fullMonthUserIds.has(userId);
              const displayEntries = getEntriesForDisplay(
                userGroup.entries,
                showFullMonth,
                userId,
              );

              return (
              <div key={userId} className="user-schedule-card">
                <div
                  className={`user-schedule-header${
                    showFullMonth ? " user-schedule-header--expanded" : ""
                  }`}
                  onClick={() => toggleFullMonth(userId)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleFullMonth(userId);
                    }
                  }}
                >
                  <h3>{userGroup.fullName || "Nieznany użytkownik"}</h3>
                  <div className="user-totals">
                    <span className="hour-badge remote">
                      Zdalne: {userGroup.totalRemote.toFixed(2)}h
                    </span>
                    <span className="hour-badge onsite">
                      Stacjonarne: {userGroup.totalOnsite.toFixed(2)}h
                    </span>
                    <span className="hour-badge total">
                      Razem: {userGroup.totalHours.toFixed(2)}h
                    </span>
                  </div>
                </div>
                <div className="user-schedule-details">
                  <table className="schedule-table-compact">
                    <thead>
                      <tr>
                        {showFullMonth && <th>Dzień</th>}
                        <th>Data</th>
                        <th>Typ dnia</th>
                        <th>Zdalne</th>
                        <th>Stacjonarne</th>
                        <th>Suma</th>
                        <th>Komentarz</th>
                        <th>Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayEntries.map((entry) =>
                        renderEntryRow(entry, { showDayName: showFullMonth }),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
            })
          )}
        </div>
      ) : (
        <div className="single-user-schedule">
          <div className="schedule-summary">
            <div className="summary-card">
              <h3
                className={
                  selectedUserShowFullMonth
                    ? "summary-card-title--expanded"
                    : ""
                }
                onClick={() => toggleFullMonth(selectedUser.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleFullMonth(selectedUser.id);
                  }
                }}
              >
                Podsumowanie dla: {selectedUser.firstName}{" "}
                {selectedUser.lastName}
              </h3>
              <div className="summary-row">
                <span>Godziny zdalne:</span>
                <strong>
                  {groupedByUser[selectedUser.id]?.totalRemote.toFixed(2) ||
                    "0.00"}
                  h
                </strong>
              </div>
              <div className="summary-row">
                <span>Godziny stacjonarne:</span>
                <strong>
                  {groupedByUser[selectedUser.id]?.totalOnsite.toFixed(2) ||
                    "0.00"}
                  h
                </strong>
              </div>
              <div className="summary-row total">
                <span>Razem:</span>
                <strong>
                  {groupedByUser[selectedUser.id]?.totalHours.toFixed(2) ||
                    "0.00"}
                  h
                </strong>
              </div>
            </div>
          </div>

          <div className="schedule-table-container">
            <table className="schedule-table">
              <thead>
                <tr>
                  <th>Dzień</th>
                  <th>Data</th>
                  <th>Typ dnia</th>
                  <th>Godziny zdalne</th>
                  <th>Godziny stacjonarne</th>
                  <th>Suma (dzień)</th>
                  <th>Komentarz</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {selectedUserDisplayEntries.length > 0 ? (
                  selectedUserDisplayEntries.map((entry) =>
                    renderEntryRow(entry, { showDayName: true }),
                  )
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data-cell">
                      Brak danych dla wybranego miesiąca
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleAdmin;
